import { NotificationClientService, NotificationType } from '@rightship-tech/common-ui/services/basic/notification-client.service';
import { EntityTypes } from '@rightship-tech/common-ui/models/basic.models';
import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject, transient } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { AttachmentFile } from "@rightship-tech/common-ui/models/attachments.models";
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { CustomValidationRenderer } from '@rightship-tech/common-ui/client-validation-system/custom-validation-renderer';
import { RegexConstants } from '@rightship-tech/common-ui/constants/regex.constants';
import { Messages } from '@rightship-tech/common-ui/constants/event-messages.constants';
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";
import { DocumentManagementEntityTypeEnum, DocumentMetaModel, FormMetaModel, RequirementModel } from "../../../models/document-management.models";
import { SubtasksApi } from '../../../apis/subtasks.api';
import { AddSubtaskRequest } from "../../../models/subtasks.models";
import Enumerable from 'linq';
import { LookupValueRequest, LookupValueResponse } from '@rightship-tech/common-ui/models/lookup.models';
import { LookupClientService } from '@rightship-tech/common-ui/services/lookups/lookup-client.service';
import { RsUsersApi } from '../../../apis/rs-users.api';
import { EmailRecipientTypeEnum } from '../../../models/common.models';
import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';

export type DocumentModel = { id: number, name: string, email: string };

@autoinject
@transient()
export class DialogDocumentEnquiry {

    private _formTypes: { [id: number]: string } = {};
    private _documentTypes: { [id: number]: string } = {};
    private _userEmails: { [id: number]: string } = {};
    private _subtaskRequestModel: AddSubtaskRequest;
    private _validator: ValidationController;
    public vesselId: number;
    public vesselName: string;
    public vesselIdentifierType: string;
    public vesselIdentifier: string;
    public requirements: RequirementModel[];
    public documentRequirements: DocumentModel[];
    public customerName: string;
    public accept: string = "application/pdf,image/png,image/jpeg,image/jpg,application/zip,application/x-zip-compressed,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.ms-outlook,.msg";
    public message: string;
    public emailSubject = "Document Inquiry - ";
    public attachments: AttachmentFile[] = [];
    public recipientEmails: { email: string, emailRecipientTypeId: number, documentIds?: number[] }[] = [{ email: "", emailRecipientTypeId: EmailRecipientTypeEnum.To }];
    public selectededDocuments: DocumentModel[] = [{ id: null, name: null, email: null }];
    public emailIdentifier?: string;

    constructor(validationControllerFactory: ValidationControllerFactory, public dialogClientService: DialogClientService,
        private userClientService: UserClientService, private subtasksApi: SubtasksApi, private eventAggregator: EventAggregator,
        private notificationClientService: NotificationClientService, private lookupClientService: LookupClientService,
        private rsUsersApi: RsUsersApi, private googleAnalyticsService: GoogleAnalyticsService) {
        this._validator = validationControllerFactory.createForCurrentScope();
        this._validator.addRenderer(new CustomValidationRenderer());
    }

    async activate(model: { vesselId: number, vesselName: string, vesselIdentifierType: string, vesselIdentifier: string, requirements: RequirementModel[] }) {
        this.vesselId = model.vesselId;
        this.vesselName = model.vesselName;
        this.vesselIdentifierType = model.vesselIdentifierType;
        this.vesselIdentifier = model.vesselIdentifier;
        this.requirements = model.requirements;
    }

    async attached() {

        await this.loadDocumentNames(this.requirements);
        await this.loadUserEmails(this.requirements);

        //Filter document requirements by those that have been submitted
        this.documentRequirements = this.requirements.filter(item => {
            if (!item.meta) {
                return false;
            }
    
            if (item.entityType == DocumentManagementEntityTypeEnum.VesselForm) {
                const meta: FormMetaModel = item.meta as FormMetaModel;
                return meta.onboardingRequestId > 0 && meta.formStatus != 'Pending Form Input';
            } else if (item.entityType == DocumentManagementEntityTypeEnum.VesselDocument) {
                const meta: DocumentMetaModel = item.meta as DocumentMetaModel;
                return meta.entityAttachmentIds?.length > 0;
            }
        })
        .map(x => {

            return {
                id: this.getDocumentId(x),
                name: this.getDocumentName(x),
                email: this._userEmails && Object.keys(this._userEmails).indexOf(x.uploadedByUserId?.toString()) > -1? 
                    this._userEmails[x.uploadedByUserId]: null
            }
        });

        //Get customer name
        const currentUser = await this.userClientService.currentUser;
        this.customerName = currentUser.businessUnit ? currentUser.businessUnit.customerName : 'RightShip User';
        this.emailSubject += this.customerName;
        
        //Set message
        this.message = `Good day,<br/>${this.customerName} has an inquiry about the following document/s:<br/>
        {{Selected document(s) are inserted here}}`
    }

    private getDocumentId(item: RequirementModel) {
        if (item.entityType == DocumentManagementEntityTypeEnum.VesselForm) {
            const id = (<FormMetaModel>item.meta)?.formTypeId;
            return id;
        }
        else if (item.entityType == DocumentManagementEntityTypeEnum.VesselDocument) {
            const id = (<DocumentMetaModel>item.meta)?.documentTypeId;
            return id;
        }
    } 

    private getDocumentName(item: RequirementModel): string {
        if (item.entityType == DocumentManagementEntityTypeEnum.VesselForm) {
            const id = (<FormMetaModel>item.meta)?.formTypeId;
            return this._formTypes[id] ?? item.requirementName;
        }
        else if (item.entityType == DocumentManagementEntityTypeEnum.VesselDocument) {
            const id = (<DocumentMetaModel>item.meta)?.documentTypeId;
            return this._documentTypes[id] ?? item.requirementName;
        }
    }

    private async loadDocumentNames(requirements: RequirementModel[]) {
        const formsLookupQuery = "CustomerFormTypes";
        const documentsLookupQuery = "AttachmentTypes";
        const lookupParams = (item: RequirementModel) => {
            if (item.entityType == DocumentManagementEntityTypeEnum.VesselForm)
                return `${item.requirementCustomerId}`;
            if (item.entityType == DocumentManagementEntityTypeEnum.VesselDocument)
                return `${DocumentManagementEntityTypeEnum.DocumentRequirementSubscriptionVessel}, false`;
        };
        const lookupQuery = (item: RequirementModel) => {
            if (item.entityType == DocumentManagementEntityTypeEnum.VesselForm)
                return formsLookupQuery;
            if (item.entityType == DocumentManagementEntityTypeEnum.VesselDocument)
                return documentsLookupQuery;
        };

        const lookups = requirements.map(x => {
            return {
                query: lookupQuery(x),
                params: lookupParams(x)
            }
        });

        const lookupQueries = Enumerable.from(lookups).groupBy(x => { return { query: x.query, params: x.params }; }, null,
            (key, group) => { return { query: key.query, params: key.params }; },
            (key) => { return `${key.query}__${key.params}`; }
        );

        let promises: Promise<LookupValueResponse>[] = [];
        lookupQueries.forEach(x => {
            const promise = this.lookupClientService.getLookup(new LookupValueRequest(x.query, x.params));
            promises.push(promise);
        });

        const responses = await Promise.all(promises);

        responses.forEach(response => {
            if (response.lookupValueQuery == formsLookupQuery) {
                response.activeValues.forEach(x => {
                    this._formTypes[x.id] = x.name;
                });
            }
            else if (response.lookupValueQuery == documentsLookupQuery) {
                response.activeValues.forEach(x => {
                    this._documentTypes[x.id] = x.name;
                });
            }
        });
    }

    public async loadUserEmails(requirements: RequirementModel[]) {
        const userIds = Enumerable.from(requirements).select(x => x.uploadedByUserId).distinct().toArray();
        const response = await this.rsUsersApi.getUsersByIds(userIds);

        response?.users?.filter(x => x.userId > 0).forEach(user => {
            this._userEmails[user?.userId] = user?.email;
        });
    }

    //Get validation rules
    getRules(){
        let emailRegex = new RegExp(RegexConstants.Patterns.EmailRfc5322);

        ValidationRules
            .ensure<DialogDocumentEnquiry, string>(x => x.message)
            .required()
            .withMessage("Please enter message")
            .on(this);

        this.recipientEmails.forEach((recipientEmails) => {
            ValidationRules
                .ensure('recipientEmails')
                .required()
                .satisfies(email => {
                    return emailRegex.test(email)
                })
                .withMessage('Email is invalid')
                .on(recipientEmails);
        });    

        this._validator.reset();
        this._validator.addObject(this);    
    }

    //Submit
    async submit() {
        this.getRules();
        let validator = await this._validator.validate();
        if (validator.valid) {
            try {

                //Replace message '{{Selected document(s) are inserted here}}' with selected documents
                const msg = this.message.replace('{{Selected document(s) are inserted here}}', this.selectededDocuments.map(d => d.name).join('<br/>'));
                const subject = `${this.emailSubject} for ${this.vesselName} (${this.vesselIdentifierType} ${this.vesselIdentifier})`;

                this._subtaskRequestModel = {
                    entityId: this.vesselId,
                    entityTypeId: EntityTypes.Vessel,
                    emails: this.recipientEmails,
                    isInternal: false,
                    resendEmail: false,
                    skipInvitation: true,
                    isCustomerRFI: false,
                    changeStatusToPendingReview: false,
                    changeStatusToAwaitingResponse: false,
                    message: msg,
                    comment: "",
                    attachments: [],
                    files: this.getAttachmentTypes(),
                    statusSnippet: "",
                    name: "",
                    submit: true,
                    subtaskId: null,
                    subtaskTypes: [],
                    subtaskCorrespondenceId: null,
                    vetTaskIds: [],
                    dataSourceId: 0,
                    isPriority: false,
                    orderGuid: "",
                    subject: subject,
                    messageSenderEmail: this.userClientService?.currentUser?.user?.email
                }

                //Add sutask
                await this.subtasksApi.addSubtask(this._subtaskRequestModel);
                this.notificationClientService.show("Document Inquiry has been sent successfully", "", NotificationType.Success);

                //Send event to google analytics
                this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_inquiry_submit", {});

                //Close dialog
                this.cancel();

                //Call this event aggregator to change tab to 'Message Centre'
                this.eventAggregator.publish(Messages.changeTab, { });
            } catch (error) {
                console.error(error);
                this.notificationClientService.show("An error has occurd", "", NotificationType.Error);
            }
        }
    }

    //Cancel
    public cancel() {
        this.dialogClientService.hide();
    }

    //Add email
    public addEmail(email?: string, documentId?: number) {
        this.recipientEmails.push({ email: email, emailRecipientTypeId: EmailRecipientTypeEnum.To, documentIds: [documentId] });
    }

    //Remove email
    public removeEmail(i: number) {
        this.recipientEmails.splice(i, 1);
    }

    //Add document
    public addDocument() {
        this.selectededDocuments.push({ id: null, name: null, email: null });

        //Send event to google analytics
        this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_inquiry_add_document", {});
    }

    //Remove document
    public removeDocument(i: number, document: DocumentModel) {
        this.selectededDocuments.splice(i, 1);
        this.removeDocumentEmail(document.id);
    }

    private removeDocumentEmail(documentId: number)
    {
    
        const emailIndex = this.recipientEmails?.findIndex(e => e?.documentIds?.indexOf(documentId) > -1);
        if(emailIndex >= 0)
        {
            this.recipientEmails[emailIndex].documentIds = this.recipientEmails[emailIndex].documentIds.filter(d => d != documentId);
            if(this.recipientEmails[emailIndex].documentIds.length == 0)
                this.removeEmail(emailIndex);
        }
    }

    //Document selected
    public documentSelected(index: number, document: DocumentModel) {
        if(index >= 0 )
        {
            if(this.selectededDocuments[index]?.id != document.id)
            {                
                this.removeDocumentEmail(this.selectededDocuments[index]?.id);
            }
            this.selectededDocuments[index] = document;
            if(document?.email && document?.email != "")
            {
                const emailIndex = this.recipientEmails?.findIndex(e => !e?.email || e?.email == "" //empty email
                    || e?.documentIds?.indexOf(document.id) > -1 //email by documentid
                    || e.email == document.email //email already exists
                );
                if(emailIndex >= 0)
                {
                    this.recipientEmails[emailIndex].email = document.email;
                    if(!this.recipientEmails[emailIndex].documentIds)
                        this.recipientEmails[emailIndex].documentIds = [];
                    this.recipientEmails[emailIndex].documentIds.push(document.id);
                }
                else
                {
                    this.addEmail(document.email, document.id);
                }
            }
        }
    }

    //Get attachment types
    public getAttachmentTypes(){
        if(this.attachments && this.attachments.length > 0)
        {
            let attachmentFiles = this.attachments.map(c => {
                return { attachementTypeId: c.attachmentTypeId, file: c as File }
            });
            return attachmentFiles;
        }

        return [];
    }
}
